/**
 * PLAT-9968 — composition quick-edit field walker.
 *
 * Sister to `extractTranslatableElements` but scoped to a single
 * composition placement and emitting every `allowQuickEdit: true` text
 * control (translatable AND quick-edit-only). The quick-edit popover
 * consumes this to populate one row per field; translatable rows get a
 * translated-value cell on top of the original-value cell, quick-edit-only
 * rows get just the original.
 *
 * Reuses `validateTranslationFlags` so misconfigured controls (missing id,
 * non-text type, multi-path translatable) are demoted the same way the
 * authoring UI and `extractTranslatableElements` already demote them. No
 * preset short-circuit here — the popover needs every editable row, and the
 * resolver decides at render time whether a row's translated value comes
 * from a phrase, a preset, or is absent.
 */
import { validateTranslationFlags } from '@src/components/sidepanel/compositionNormalizer/validateTranslationFlags';
import { LogManager } from '@src/utility/Logger';

import { traverseCompositionElementPath } from './compositionElementPath';

import type {
  AITextBaseTextScope,
  AITextControlConfiguration,
  ControlConfiguration,
} from '@src/components/sidepanel/CompositionSettings/types';

const logger = LogManager.getLogger(
  'translation:extractCompositionQuickEditFields',
);

/**
 * PLAT-10913 — per-row config for an `aiText` control, mirrored off
 * `AITextControlConfiguration` so the popover can render the AI control
 * without reaching back into `compositionControls`.
 */
export interface CompositionQuickEditAIText {
  /** DatoCMS `ai_prompt` record id — the prompt this row generates with. */
  promptId: string;
  /** Human-readable mirror of the prompt's title. Display only. */
  promptTitle?: string;
  /** Base-text mode the control opens on. */
  defaultBaseTextScope: AITextBaseTextScope;
}

export interface CompositionQuickEditField {
  /** Stable control id from DatoCMS. */
  controlId: string;
  /**
   * PLAT-10913 — control type driving this row. `'text'` for a plain
   * quick-edit field (every row before this ticket); `'aiText'` for a row the
   * AI text control owns. Consumers that only handle text keep working —
   * an `aiText` row carries the same leaf contract.
   */
  controlType: 'text' | 'aiText';
  /** Present only when `controlType === 'aiText'` (PLAT-10907 E1). */
  aiText?: CompositionQuickEditAIText;
  /** Human-friendly control label ("Speaker name", "Title"). */
  controlLabel: string;
  /** Composition template id (DatoCMS composition record). */
  compositionId: string;
  /** Stable source-element id on the placement. */
  sourceElementId: string;
  /** Source-element name on the placement ("Name", "Quote", "Organization"). */
  sourceElementName: string;
  /** Property on the source element holding the text (`"text"` in v1). */
  property: string;
  /** Whether this control opts into translation (`control.allowTranslation`). */
  isTranslatable: boolean;
  /** Current original-language value at the leaf. */
  originalValue: string;
  /**
   * `elementPaths[0]` for this control — the path the save handler walks
   * to write a new original-language value back into the video source.
   * Always a single path; multi-path translatable controls are dropped
   * by `validateTranslationFlags`.
   */
  elementPath: ReadonlyArray<number>;
  /**
   * Snapshot of the control's preset map + default value at extraction
   * time — passed straight through to `resolveCompositionFieldText` so
   * the popover renders the preset translation when applicable.
   */
  controlPreset?: {
    templateDefaultValue: string;
    translations?: Record<string, string>;
  };
}

interface ExtractParams {
  /**
   * `videoSource.elements` — the top-level array from `VideoSourceStore.source`.
   * Same shape `extractTranslatableElements` consumes.
   */
  videoSourceElements:
    | ReadonlyArray<Record<string, unknown>>
    | null
    | undefined;
  /**
   * `currentVideo.extraElementData` — bucket of per-element metadata
   * (parsed `compositionControls`, `templateId`).
   */
  extraElementData: Record<string, unknown> | null | undefined;
  /**
   * Composition placement id to extract fields for. The walker scans
   * `videoSourceElements` for the matching `type === 'composition' && id === compositionElementId`
   * and returns its quick-edit fields. Returns `[]` if no match.
   */
  compositionElementId: string;
}

/**
 * Walk one composition placement and produce the row list for the quick-edit
 * popover. Returns one entry per `allowQuickEdit: true` (and translatable
 * controls are always also quick-edit-able for the popover's purposes — they
 * are the rows that get a language toggle).
 *
 * Drops controls silently when:
 *   - the control is demoted by `validateTranslationFlags` (missing id,
 *     non-text, etc.) — same warnings the authoring UI already surfaces;
 *   - neither `allowQuickEdit` nor `allowTranslation` is set;
 *   - the resolved leaf value isn't a string or is empty (nothing to edit).
 *
 * v1 treats `allowTranslation: true && allowQuickEdit: false` as a noop in
 * the popover — translation without an edit affordance has no consumer in
 * this surface. If we later want to render a read-only translated-only row
 * the contract here changes to keep those.
 */
export function extractCompositionQuickEditFields(
  params: ExtractParams,
): CompositionQuickEditField[] {
  const { videoSourceElements, extraElementData, compositionElementId } =
    params;

  if (!Array.isArray(videoSourceElements) || videoSourceElements.length === 0) {
    return [];
  }
  if (!compositionElementId) return [];

  const placement = videoSourceElements.find(el => {
    if (!el || typeof el !== 'object') return false;
    const type = (el as { type?: unknown }).type;
    const id = (el as { id?: unknown }).id;
    return type === 'composition' && id === compositionElementId;
  });
  if (!placement) return [];

  const meta = readMeta(extraElementData, compositionElementId);
  if (!meta) return [];
  const { templateId, controls } = meta;

  const { sanitized } = validateTranslationFlags(controls);

  const fields: CompositionQuickEditField[] = [];

  for (const control of sanitized) {
    if (!control.allowQuickEdit) continue;
    if (
      !Array.isArray(control.elementPaths) ||
      control.elementPaths.length === 0
    ) {
      continue;
    }
    if (control.elementPaths.length > 1) {
      // Validator already warned; defensively skip multi-path here too.
      continue;
    }
    const path = control.elementPaths[0];
    const leaf = traverseCompositionElementPath(placement, path);
    if (!leaf) continue;

    const aiText = readAITextConfig(control);

    const value = (leaf as Record<string, unknown>)[control.property];
    if (typeof value !== 'string') continue;
    // PLAT-10913 — an AI row survives an empty leaf. Generating is exactly how
    // you fill a card whose placeholder was cleared, so dropping the row would
    // remove the only affordance that can recover it. Plain text rows keep the
    // original "nothing to edit" guard.
    if (value.trim() === '' && !aiText) continue;

    const sourceElementId =
      typeof (leaf as { id?: unknown }).id === 'string'
        ? (leaf as { id: string }).id
        : '';
    if (sourceElementId === '') continue;

    const sourceElementName =
      typeof (leaf as { name?: unknown }).name === 'string'
        ? (leaf as { name: string }).name
        : (control.label ?? '');

    const field: CompositionQuickEditField = {
      controlId: control.id ?? '',
      controlType: aiText ? 'aiText' : 'text',
      ...(aiText ? { aiText } : {}),
      controlLabel: control.label,
      compositionId: templateId,
      sourceElementId,
      sourceElementName,
      property: control.property,
      isTranslatable: !!control.allowTranslation,
      originalValue: value,
      elementPath: path,
    };

    const defaultValue = control.default?.value;
    if (
      typeof defaultValue === 'string' &&
      defaultValue !== '' &&
      control.translations &&
      Object.keys(control.translations).length > 0
    ) {
      field.controlPreset = {
        templateDefaultValue: defaultValue,
        translations: { ...control.translations },
      };
    }

    fields.push(field);
  }

  if (fields.length === 0) {
    logger.debug(
      'extractCompositionQuickEditFields produced zero rows for composition',
      { compositionElementId },
    );
  }

  return fields;
}

/**
 * PLAT-10913 — mirror an `aiText` control's config onto the row, or `null`
 * for any other control type. A control that declares `aiText` without a
 * `promptId` has nothing to generate with, so it degrades to a plain
 * quick-edit text row (the text is still hand-editable) rather than being
 * dropped, and the misconfiguration is warned about once per extraction.
 */
function readAITextConfig(
  control: ControlConfiguration,
): CompositionQuickEditAIText | null {
  if (control.type !== 'aiText') return null;
  const { promptId, promptTitle, defaultBaseTextScope } =
    control as AITextControlConfiguration;
  if (typeof promptId !== 'string' || promptId === '') {
    logger.warn('aiText control has no promptId — rendering as plain text', {
      controlId: control.id ?? '',
      controlLabel: control.label,
    });
    return null;
  }
  const mode: AITextBaseTextScope =
    defaultBaseTextScope === 'clip' ? 'clip' : 'selection';
  return {
    promptId,
    ...(promptTitle ? { promptTitle } : {}),
    defaultBaseTextScope: mode,
  };
}

interface CompositionMeta {
  templateId: string;
  controls: ControlConfiguration[];
}

function readMeta(
  extraElementData: Record<string, unknown> | null | undefined,
  compositionElementId: string,
): CompositionMeta | null {
  if (!extraElementData || typeof extraElementData !== 'object') return null;
  const bucket = (extraElementData as Record<string, unknown>)[
    compositionElementId
  ];
  if (!bucket || typeof bucket !== 'object') return null;
  const templateId = (bucket as { templateId?: unknown }).templateId;
  const controls = (bucket as { compositionControls?: unknown })
    .compositionControls;
  if (typeof templateId !== 'string' || templateId === '') return null;
  if (!Array.isArray(controls)) return null;
  return { templateId, controls: controls as ControlConfiguration[] };
}
